/**
 * User-event handlers for the report editor's measurement + context panels.
 *
 * - handleMeasurementClick: row click → start drawing OR navigate the viewer
 *   to the existing annotation
 * - handleMeasurementValueChange: manual edit (incl. clear-to-null)
 * - handleContextChange: context field edit
 *
 * Pure callbacks (no effects). All measurement-session effects that fold
 * Redux results back into `currentReport` live in `useMeasurementHydration`.
 */

import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { MeasurementRowModel } from '@/features/report';
import {
  startMeasurement,
  cancelMeasurement,
  selectActiveMeasurement,
  selectAnnotation,
  selectAnnotationToMeasurement,
} from '@/state/slices/measurementSession';
import { getSimpsonMeasurementType } from '@/utils/measurementToolMapping';
import { Measurement, Report as MReport } from '@/models/report';
import { MeasurementLabel, MeasurementGroup } from '@/models/template';
import { commitHistoryStep } from '@/features/study-detail/history/historyStore';
import { createWorksheetStep } from '@/features/study-detail/history/worksheetSteps';
import { getWorksheetSnapshot } from '@/features/study-detail/history/worksheetApplier';
import { deleteAILine } from '@/features/study-detail/history/annotationLifecycle';
import type { RootState } from '@/state/store';
import type { CascadeAction } from '@/features/report/derived/applyCascade';
import { runCascade } from '../utils/runCascade';

interface UseMeasurementHandlersArgs {
  studyUuid: string;
  currentReport: MReport | null;
  setCurrentReport: (next: MReport) => void;
  /** Per-canonical resolved row state — the SAME map the panel renders from,
   *  so click behavior (navigation, arming) can never disagree with what the
   *  row displays. */
  rowModels: Map<string, MeasurementRowModel>;
  /** Worksheet groups — the engine's scope and formula source (AP-731 R3). */
  measurementGroups?: MeasurementGroup[];
  patientSex?: string | null;
  onGoToAnnotation?: (dicomUuid: string, frameNumber: number) => void;
  canEdit: boolean;
}

export interface MeasurementHandlers {
  handleMeasurementClick: (measurementLabel: MeasurementLabel) => void;
  /** A row's value input gained focus — focusing disarms any armed row and
   *  deselects any selected trace (I7). Covers Tab-focus, which never passes
   *  through the viewer's outside-click disarm path. */
  handleMeasurementFocus: () => void;
  handleMeasurementValueChange: (
    canonicalMeasurement: string,
    value: number | null,
    unit: string | null
  ) => void;
  /** Reset a diverged computed row to its calculated value (AP-731 R17). */
  handleConverge: (canonicalMeasurement: string) => void;
  handleContextChange: (contextClass: string, value: string) => void;
}

/** AP-773 (A5): a committed value is its own undo step; call before the write. */
function commitValueStep(key: string | null | undefined): void {
  const before = getWorksheetSnapshot();
  if (!before) return;
  commitHistoryStep(
    createWorksheetStep('A5', before, `${key ?? 'measurement'} value`)
  );
}

export function useMeasurementHandlers({
  studyUuid,
  currentReport,
  setCurrentReport,
  rowModels,
  measurementGroups,
  patientSex,
  onGoToAnnotation,
  canEdit,
}: UseMeasurementHandlersArgs): MeasurementHandlers {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const activeMeasurement = useSelector(selectActiveMeasurement);
  const annotationToMeasurement = useSelector(selectAnnotationToMeasurement);

  // Resolve the viewer annotation UID for a row. Prefer the runtime
  // annotation→canonical map (covers freshly-drawn/adopted lines with
  // non-deterministic UIDs), but fall back to the deterministic hydrated UID.
  // The fallback matters on the FIRST click right after the page opens: the
  // map is populated during hydration, so an early click would otherwise find
  // nothing and skip selection. Hydrated UIDs are study-scoped and
  // deterministic — mirror `hydratedAnnotationUid` / `aiHydratedAnnotationUid`
  // in the viewer. `isAI` picks the prefix (AI lines live under `ai-hydrated-`).
  const findAnnotationUIDForCanonical = useCallback(
    (canonical: string | null | undefined, isAI = false) => {
      if (!canonical) return null;
      const mapped = annotationToMeasurement
        ? (Object.entries(annotationToMeasurement).find(
            ([, canon]) => canon === canonical
          )?.[0] ?? null)
        : null;
      if (mapped) return mapped;
      if (!studyUuid) return null;
      return isAI
        ? `ai-hydrated-${studyUuid}-${canonical}`
        : `hydrated-${studyUuid}-${canonical}`;
    },
    [annotationToMeasurement, studyUuid]
  );

  const handleMeasurementClick = useCallback(
    (measurementLabel: MeasurementLabel) => {
      // If this row's tool is already armed, a click disarms it — before any
      // navigation logic. Live Redux state (not a ref) so it stays
      // correct when Escape or a completed draw already cleared the selection.
      if (
        activeMeasurement &&
        activeMeasurement.canonicalMeasurement ===
          measurementLabel.canonicalMeasurement
      ) {
        dispatch(cancelMeasurement());
        return;
      }

      const model = measurementLabel.canonicalMeasurement
        ? rowModels.get(measurementLabel.canonicalMeasurement)
        : undefined;

      // Unless this click arms the row, it disarms any armed one (AP-803, I7).
      if (
        model?.annotation ||
        !model?.tool ||
        currentReport?.isFinalized ||
        !canEdit
      ) {
        dispatch(cancelMeasurement());
      }

      // An annotated row ALWAYS navigates to its DICOM — the model resolved
      // human-vs-AI precedence (and, for AI, picked the best ANNOTATED
      // variant). A plain click never re-arms the drawing tool; re-measuring
      // an existing annotation will be a separate gesture (e.g. ctrl+click)
      // added later.
      if (model?.annotation) {
        onGoToAnnotation?.(
          model.annotation.dicomUuid,
          model.annotation.frameNumber
        );
        const uid = findAnnotationUIDForCanonical(
          measurementLabel.canonicalMeasurement,
          model.annotation.origin === 'ai'
        );
        if (uid) dispatch(selectAnnotation(uid));
        return;
      }

      // No annotation — block drawing when finalized or read-only role.
      if (currentReport?.isFinalized || !canEdit) return;

      // Backend-driven tool, resolved in the model (row wins, null gates off)
      // — the same gate that renders the panel's outline pencil.
      const toolType = model?.tool ?? null;
      if (!toolType) return;

      const simpsonMeasurementType =
        toolType === 'simpson'
          ? getSimpsonMeasurementType(measurementLabel.label)
          : undefined;

      dispatch(
        startMeasurement({
          canonicalMeasurement: measurementLabel.canonicalMeasurement ?? '',
          measurementLabel: measurementLabel.label,
          toolType,
          unit: measurementLabel.unit || 'cm',
          precision: measurementLabel.precision ?? undefined,
          simpsonMeasurementType: simpsonMeasurementType ?? undefined,
        })
      );
    },
    [
      activeMeasurement,
      canEdit,
      currentReport?.isFinalized,
      rowModels,
      dispatch,
      onGoToAnnotation,
      findAnnotationUIDForCanonical,
    ]
  );

  // Focusing a value input clears the other emphases (I7): disarm (the
  // cancelMeasurement status flip also deactivates the Cornerstone tool via
  // useMeasurementToolActivation) and deselect (the viewer's selection effect
  // clears the highlight). Both dispatches are idempotent no-ops when idle.
  const handleMeasurementFocus = useCallback(() => {
    dispatch(cancelMeasurement());
    dispatch(selectAnnotation(null));
  }, [dispatch]);

  // AP-731 §1 — every reader write runs the cascade, with the snapshot taken
  // from the report as it was before the change.
  const cascade = useCallback(
    (
      updated: Measurement[],
      target: string,
      action: CascadeAction
    ): Measurement[] => {
      if (!currentReport) return updated;
      return runCascade({
        report: currentReport,
        updated,
        target,
        action,
        measurementGroups,
        patientSex,
      });
    },
    [currentReport, measurementGroups, patientSex]
  );

  // Typed values and converge: a MANUAL value, row updated or created, cascaded.
  const writeManualValue = useCallback(
    (
      canonicalMeasurement: string,
      value: number | null,
      unit: string,
      action: CascadeAction
    ) => {
      if (!currentReport) return;
      const now = new Date().toISOString();
      const exists = currentReport.measurements.some(
        (m) => m.canonicalMeasurement === canonicalMeasurement
      );
      const updated: Measurement[] = exists
        ? currentReport.measurements.map((m) =>
            m.canonicalMeasurement === canonicalMeasurement
              ? { ...m, value, valueSource: 'MANUAL' as const, updatedAt: now }
              : m
          )
        : [
            ...currentReport.measurements,
            {
              // No uuid, so the backend creates it (an empty string fails
              // DRF's UUID validator). The Context section finds its rows by
              // key, so a new row carries it before the refetch supplies it.
              canonicalMeasurement,
              canonicalMeasurementKey:
                measurementGroups
                  ?.flatMap((g) => g.measurementLabels)
                  .find(
                    (ml) => ml.canonicalMeasurement === canonicalMeasurement
                  )?.canonicalMeasurementKey ?? null,
              value,
              valueSource: 'MANUAL' as const,
              unit,
              createdAt: now,
              updatedAt: now,
              annotation: null,
            },
          ];
      setCurrentReport({
        ...currentReport,
        measurements: cascade(updated, canonicalMeasurement, action),
      });
    },
    [currentReport, setCurrentReport, cascade, measurementGroups]
  );

  // Manual edit. A null value represents "clear this row".
  const handleMeasurementValueChange = useCallback(
    (
      canonicalMeasurement: string,
      value: number | null,
      unit: string | null
    ) => {
      if (!currentReport) return;

      // Clearing an AI value deletes its AI line too (one undo restores both).
      const origin = rowModels.get(canonicalMeasurement)?.annotation?.origin;
      if (value === null && origin === 'ai') {
        const uid = findAnnotationUIDForCanonical(canonicalMeasurement, true);
        if (uid && deleteAILine(uid, store, canonicalMeasurement)) return;
      }

      const existingMeasurement = currentReport.measurements.find(
        (m) => m.canonicalMeasurement === canonicalMeasurement
      );
      if (
        !existingMeasurement ||
        existingMeasurement.value !== value ||
        (existingMeasurement.unit ?? null) !==
          (unit ?? existingMeasurement.unit ?? null)
      ) {
        commitValueStep(existingMeasurement?.canonicalMeasurementKey);
      }

      // No existing row — clearing an AI-only value is a no-op; avoid
      // creating a phantom row.
      if (!existingMeasurement && value === null) return;

      // MANUAL even when cleared, so SR/AI readings never return (AP-613, AP-731 R8).
      writeManualValue(
        canonicalMeasurement,
        value,
        unit ?? '',
        value === null ? 'delete' : 'edit'
      );
    },
    [
      currentReport,
      writeManualValue,
      findAnnotationUIDForCanonical,
      store,
      rowModels,
    ]
  );

  // R17 — write the formula's result, then cascade. Divergence then reads
  // converged because the value equals the formula; there is no state to set.
  const handleConverge = useCallback(
    (canonicalMeasurement: string) => {
      if (!currentReport) return;
      const model = rowModels.get(canonicalMeasurement);
      const calculated = model?.calculated;
      if (calculated === null || calculated === undefined) return;

      // A converge is its own undo step, like a typed value.
      commitValueStep(
        currentReport.measurements.find(
          (m) => m.canonicalMeasurement === canonicalMeasurement
        )?.canonicalMeasurementKey
      );

      // A computed row can be diverged on an AI-only value, with no report row
      // to write into — the write creates one, or converge would do nothing.
      writeManualValue(
        canonicalMeasurement,
        calculated,
        model?.unit ?? '',
        'converge'
      );
    },
    [currentReport, rowModels, writeManualValue]
  );

  const handleContextChange = useCallback(
    (contextClass: string, value: string) => {
      if (!currentReport) return;
      // AP-773 (A8): the context row commits on blur; one commit that
      // changes the text = one step.
      const existing = currentReport.context.find(
        (c) => c.contextClass === contextClass
      );
      const before = getWorksheetSnapshot();
      if (before && existing && existing.value !== value) {
        commitHistoryStep(createWorksheetStep('A8', before, 'context'));
      }
      const updatedContext = currentReport.context.map((c) =>
        c.contextClass === contextClass ? { ...c, value } : c
      );
      setCurrentReport({ ...currentReport, context: updatedContext });
    },
    [currentReport, setCurrentReport]
  );

  return {
    handleMeasurementClick,
    handleMeasurementFocus,
    handleMeasurementValueChange,
    handleConverge,
    handleContextChange,
  };
}
